"use client";

import {
  ArrowDown,
  ArrowRight,
  CarFront,
  Heart,
  MapPin,
  Menu,
  Search,
  ShieldCheck,
  X,
} from "lucide-react";
import { useState, type FormEvent, type ReactNode } from "react";

import StorefrontAdBanner from "@/components/store/StorefrontAdBanner";
import type { VehicleLeadMode } from "@/components/store/VehicleLeadModal";
import type { PublicStoreCampaign } from "@/types/public-campaign";
import type { PublicProduct, PublicStore } from "@/types/store";
import AutomotiveFooter from "./AutomotiveFooter";
import AutomotiveVehicleCard from "./AutomotiveVehicleCard";
import styles from "./automotive.module.css";
import { dedicatedStoreTheme } from "../dedicated-store-theme";

type Filters = {
  query: string;
  brand: string;
  model: string;
  year: string;
  minPrice: string;
  maxPrice: string;
};
const emptyFilters: Filters = {
  query: "",
  brand: "",
  model: "",
  year: "",
  minPrice: "",
  maxPrice: "",
};

type Props = {
  store: PublicStore;
  primaryColor?: string;
  contact: ReactNode;
  headerContact: ReactNode;
  reviews: ReactNode;
  formattedCnpj: string;
  attendanceLabel: string;
  instagramUrl: string;
  facebookUrl: string;
  favoriteProductIds: number[];
  onToggleFavorite: (id: number) => void;
  onOpenProduct: (product: PublicProduct) => void;
  onCampaignProducts: (ids: number[]) => void;
  onCampaignLead: (campaign: PublicStoreCampaign) => void;
  onVehicleLead: (mode: VehicleLeadMode) => void;
  onVerification: () => void;
};

export default function AutomotiveStorefrontHome({
  store,
  primaryColor = "",
  contact,
  headerContact,
  reviews,
  formattedCnpj,
  attendanceLabel,
  instagramUrl,
  facebookUrl,
  favoriteProductIds,
  onToggleFavorite,
  onOpenProduct,
  onCampaignProducts,
  onCampaignLead,
  onVehicleLead,
  onVerification,
}: Props) {
  const { company } = store;
  const location = [company.city, company.state].filter(Boolean).join(", ");
  const [draft, setDraft] = useState<Filters>(emptyFilters);
  const [filters, setFilters] = useState<Filters>(emptyFilters);
  const [categoryId, setCategoryId] = useState<number | null>(null);
  const [favoritesOnly, setFavoritesOnly] = useState(false);
  const [visibleCount, setVisibleCount] = useState(9);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const brands = [
    ...new Set(
      store.products
        .map((p) => p.business_attributes.brand)
        .filter((v): v is string => Boolean(v)),
    ),
  ].sort((a, b) => a.localeCompare(b, "pt-BR"));
  const models = [
    ...new Set(
      store.products
        .filter(
          (p) => !draft.brand || p.business_attributes.brand === draft.brand,
        )
        .map((p) => p.business_attributes.model)
        .filter((v): v is string => Boolean(v)),
    ),
  ].sort((a, b) => a.localeCompare(b, "pt-BR"));
  const years = [
    ...new Set(
      store.products
        .map(
          (p) =>
            p.business_attributes.model_year ??
            p.business_attributes.manufacture_year,
        )
        .filter((v): v is number => Number.isFinite(v)),
    ),
  ].sort((a, b) => b - a);
  const featured = store.products.filter((p) => p.is_featured);
  const address = [
    [company.address, company.number].filter(Boolean).join(", "),
    company.district,
    [company.city, company.state].filter(Boolean).join(" / "),
    company.cep && `CEP ${company.cep}`,
  ]
    .filter(Boolean)
    .join(" · ");
  const categories = store.categories.filter((category) =>
    store.products.some((p) => p.category === category.id),
  );
  const hasContact = Boolean(company.whatsapp || company.whatsapps?.length);
  const query = filters.query.trim().toLocaleLowerCase("pt-BR");
  const filteredProducts = store.products.filter((product) => {
    const attributes = product.business_attributes;
    const price = Number(product.current_price);
    return (
      (!query ||
        [
          product.name,
          product.reference_code,
          product.short_description,
          attributes.brand,
          attributes.model,
          attributes.version,
        ]
          .filter(Boolean)
          .join(" ")
          .toLocaleLowerCase("pt-BR")
          .includes(query)) &&
      (!filters.brand || attributes.brand === filters.brand) &&
      (!filters.model || attributes.model === filters.model) &&
      (!filters.year ||
        String(attributes.model_year ?? attributes.manufacture_year) ===
          filters.year) &&
      (!filters.minPrice || price >= Number(filters.minPrice)) &&
      (!filters.maxPrice || price <= Number(filters.maxPrice)) &&
      (categoryId === null || product.category === categoryId) &&
      (!favoritesOnly || favoriteProductIds.includes(product.id))
    );
  });
  const hasFilters =
    Object.values(filters).some(Boolean) ||
    categoryId !== null ||
    favoritesOnly;

  function scrollToStock() {
    document.getElementById("catalogo")?.scrollIntoView({
      behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches
        ? "auto"
        : "smooth",
    });
  }
  function resetFilters() {
    setDraft(emptyFilters);
    setFilters(emptyFilters);
    setCategoryId(null);
    setFavoritesOnly(false);
    setVisibleCount(9);
  }
  function searchStock(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setFilters(draft);
    setCategoryId(null);
    setFavoritesOnly(false);
    setVisibleCount(9);
    scrollToStock();
  }
  function browseCategory(id: number) {
    resetFilters();
    setCategoryId(id);
    scrollToStock();
  }
  function campaign(
    placement:
      "after_featured" | "between_most_viewed_and_categories" | "before_footer",
  ) {
    const item = store.campaigns?.banners[placement];
    return (
      <StorefrontAdBanner
        campaign={item}
        onProducts={onCampaignProducts}
        onLead={item ? () => onCampaignLead(item) : undefined}
        className={styles.campaign}
      />
    );
  }
  function grid(products: PublicProduct[]) {
    return (
      <div className={styles.vehicleGrid}>
        {products.map((product) => (
          <AutomotiveVehicleCard
            key={product.id}
            product={product}
            company={company}
            isFavorite={favoriteProductIds.includes(product.id)}
            onToggleFavorite={() => onToggleFavorite(product.id)}
            onOpen={() => onOpenProduct(product)}
          />
        ))}
      </div>
    );
  }

  return (
    <div
      className={styles.home}
      style={dedicatedStoreTheme(primaryColor, "#17212b")}
      data-storefront-variant="automotive"
    >
      {
        <header className={styles.header}>
          <div className={`${styles.container} ${styles.headerRow}`}>
            <div className={styles.identity}>
              <button
                type="button"
                className={styles.menuToggle}
                aria-label={mobileMenuOpen ? "Fechar menu" : "Abrir menu"}
                aria-expanded={mobileMenuOpen}
                onClick={() => setMobileMenuOpen((open) => !open)}
              >
                {mobileMenuOpen ? (
                  <X size={22} aria-hidden="true" />
                ) : (
                  <Menu size={22} aria-hidden="true" />
                )}
              </button>

              <a href="#inicio" aria-label={`Início de ${company.name}`}>
                {company.logo_url ? (
                  <img
                    src={company.logo_url}
                    alt={company.name}
                    decoding="async"
                  />
                ) : (
                  <CarFront size={32} aria-hidden="true" />
                )}
              </a>
              <div className={styles.identityText}>
                <div className={styles.identityName}>
                  <a href="#inicio">{company.name}</a>
                </div>
                {location && (
                  <p className={styles.identityLocation}>
                    <MapPin size={12} aria-hidden="true" />
                    <span>{location}</span>
                  </p>
                )}
              </div>
            </div>
            <nav
              className={`${styles.navigation} ${
                mobileMenuOpen ? styles.navigationOpen : ""
              }`}
              aria-label="Navegação da loja"
            >
              <a href="#inicio" onClick={() => setMobileMenuOpen(false)}>
                Início
              </a>
              <a href="#catalogo" onClick={() => setMobileMenuOpen(false)}>
                Estoque
              </a>
              {featured.length > 0 && (
                <a href="#destaques" onClick={() => setMobileMenuOpen(false)}>
                  Destaques
                </a>
              )}
              <a href="#sobre" onClick={() => setMobileMenuOpen(false)}>
                A loja
              </a>
              <button
                type="button"
                onClick={() => {
                  setMobileMenuOpen(false);
                  onVehicleLead("financing");
                }}
              >
                Financiamento
              </button>
              <button
                type="button"
                onClick={() => {
                  setMobileMenuOpen(false);
                  onVehicleLead("vehicle_appraisal");
                }}
              >
                Avalie seu carro
              </button>
              <a href="#contato" onClick={() => setMobileMenuOpen(false)}>
                Contato
              </a>
            </nav>

            <div className={styles.headerActions}>
              <button
                type="button"
                className={styles.favoritesLink}
                aria-label="Ver veículos favoritos"
                onClick={() => {
                  resetFilters();
                  setFavoritesOnly(true);
                  scrollToStock();
                }}
              >
                <Heart size={20} aria-hidden="true" />
                <span>{favoriteProductIds.length}</span>
              </button>

              {headerContact}

            </div>
          </div>
        </header>
      }

      <section id="inicio">
        <h1 className={styles.srOnly}>{company.name}</h1>
        <div className={styles.bannerMedia}>
          {company.banner_url ? (
            <img
              src={company.banner_url}
              alt={`Banner de ${company.name}`}
              fetchPriority="high"
              decoding="async"
            />
          ) : (
            <div className={styles.bannerFallback}>
              <CarFront size={56} strokeWidth={1} aria-hidden="true" />
            </div>
          )}
        </div>
      </section>

      <div className={styles.container}>
        <section
          className={styles.searchPanel}
          aria-labelledby="automotive-search-title"
        >
          <div className={styles.searchHeading}>
            <Search size={22} aria-hidden="true" />
            <h2 id="automotive-search-title">Qual veículo você procura?</h2>
          </div>
          <form onSubmit={searchStock}>
            <div className={styles.filterGrid}>
              {brands.length > 0 && (
                <label>
                  Marca
                  <select
                    aria-label="Marca"
                    value={draft.brand}
                    onChange={(e) =>
                      setDraft({ ...draft, brand: e.target.value, model: "" })
                    }
                  >
                    <option value="">Todas as marcas</option>
                    {brands.map((brand) => (
                      <option key={brand}>{brand}</option>
                    ))}
                  </select>
                </label>
              )}
              {models.length > 0 && (
                <label>
                  Modelo
                  <select
                    aria-label="Modelo"
                    value={draft.model}
                    onChange={(e) =>
                      setDraft({ ...draft, model: e.target.value })
                    }
                  >
                    <option value="">Todos os modelos</option>
                    {models.map((model) => (
                      <option key={model}>{model}</option>
                    ))}
                  </select>
                </label>
              )}
              {years.length > 0 && (
                <label>
                  Ano
                  <select
                    aria-label="Ano"
                    value={draft.year}
                    onChange={(e) =>
                      setDraft({ ...draft, year: e.target.value })
                    }
                  >
                    <option value="">Todos os anos</option>
                    {years.map((year) => (
                      <option key={year}>{year}</option>
                    ))}
                  </select>
                </label>
              )}
              <label>
                Preço mínimo (R$)
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  inputMode="decimal"
                  placeholder="Sem mínimo"
                  value={draft.minPrice}
                  onChange={(e) =>
                    setDraft({ ...draft, minPrice: e.target.value })
                  }
                />
              </label>
              <label>
                Preço máximo (R$)
                <input
                  type="number"
                  min={draft.minPrice || "0"}
                  step="0.01"
                  inputMode="decimal"
                  placeholder="Sem máximo"
                  value={draft.maxPrice}
                  onChange={(e) =>
                    setDraft({ ...draft, maxPrice: e.target.value })
                  }
                />
              </label>
            </div>
            <div className={styles.searchRow}>
              <label className={styles.queryLabel}>
                <span className={styles.srOnly}>
                  Buscar por nome, versão ou referência
                </span>
                <input
                  type="search"
                  placeholder="Nome, versão ou referência"
                  value={draft.query}
                  onChange={(e) =>
                    setDraft({ ...draft, query: e.target.value })
                  }
                />
              </label>
              <button type="submit" className={styles.darkButton}>
                <Search size={18} aria-hidden="true" />
                Buscar veículos
              </button>
            </div>
          </form>
        </section>

        {featured.length > 0 && (
          <section id="destaques" className={styles.section}>
            <div className={styles.sectionHeading}>
              <div>
                <p className={styles.eyebrow}>Seleção da loja</p>
                <h2>Veículos em destaque</h2>
              </div>
              <a href="#catalogo">
                Ver estoque <ArrowRight size={18} aria-hidden="true" />
              </a>
            </div>
            {grid(featured.slice(0, 6))}
          </section>
        )}
        {campaign("after_featured")}

        {store.most_viewed.length > 0 && (
          <section
            className={styles.section}
            aria-labelledby="most-viewed-title"
          >
            <div className={styles.sectionHeading}>
              <div>
                <p className={styles.eyebrow}>Explore também</p>
                <h2 id="most-viewed-title">Mais acessados</h2>
              </div>
            </div>
            {grid(store.most_viewed.slice(0, 3))}
          </section>
        )}
        {campaign("between_most_viewed_and_categories")}

        {categories.length > 0 && (
          <section
            className={styles.categories}
            aria-label="Navegar por categoria"
          >
            <p className={styles.eyebrow}>Encontre seu estilo</p>
            <div>
              {categories.map((category) => (
                <button
                  type="button"
                  key={category.id}
                  onClick={() => browseCategory(category.id)}
                >
                  <CarFront size={24} aria-hidden="true" />
                  <span>{category.name}</span>
                  <ArrowRight size={18} aria-hidden="true" />
                </button>
              ))}
            </div>
          </section>
        )}

        <section
          id="catalogo"
          className={styles.section}
          aria-labelledby="automotive-stock-title"
        >
          <div className={styles.sectionHeading}>
            <div>
              <p className={styles.eyebrow}>
                {favoritesOnly ? "Sua seleção" : "Escolha seu próximo veículo"}
              </p>
              <h2 id="automotive-stock-title">
                {favoritesOnly ? "Seus favoritos" : "Nosso estoque"}
              </h2>
              <p role="status" aria-live="polite">
                {filteredProducts.length}{" "}
                {filteredProducts.length === 1
                  ? "veículo encontrado"
                  : "veículos encontrados"}
                {categoryId !== null
                  ? ` · ${categories.find((c) => c.id === categoryId)?.name ?? ""}`
                  : ""}
              </p>
            </div>
            {hasFilters && (
              <button
                type="button"
                className={styles.textButton}
                onClick={resetFilters}
              >
                Limpar filtros
              </button>
            )}
          </div>
          {filteredProducts.length > 0 ? (
            grid(filteredProducts.slice(0, visibleCount))
          ) : (
            <div className={styles.empty}>
              <CarFront size={40} aria-hidden="true" />
              <h3>
                {store.products.length
                  ? "Nenhum veículo nesta seleção"
                  : "Nosso estoque está sendo atualizado"}
              </h3>
              <p>
                {store.products.length
                  ? "Experimente outras opções de busca ou consulte o estoque completo."
                  : "Entre em contato com a loja para saber mais."}
              </p>
              {hasFilters && (
                <button
                  type="button"
                  className={styles.darkButton}
                  onClick={resetFilters}
                >
                  Ver todo o estoque
                </button>
              )}
            </div>
          )}
          {filteredProducts.length > visibleCount && (
            <div className={styles.loadMore}>
              <button
                type="button"
                className={styles.darkButton}
                onClick={() => setVisibleCount((count) => count + 9)}
              >
                Mostrar mais veículos <ArrowDown size={18} aria-hidden="true" />
              </button>
            </div>
          )}
        </section>
      </div>

      <section id="sobre" className={styles.about}>
        <div className={`${styles.container} ${styles.aboutGrid}`}>
          <div>
            <p className={styles.eyebrow}>Conheça a loja</p>
            <h2>{company.name}</h2>
            {company.is_verified && (
              <button
                type="button"
                onClick={onVerification}
                title="Identidade e dados cadastrais analisados pela equipe do Fornecedor.net."
                className="inline-flex appearance-none items-center gap-1.5 rounded-none border-0 bg-transparent p-0 text-xs font-medium text-emerald-700 shadow-none"
              >
                <ShieldCheck size={16} aria-hidden="true" />
                Empresa Verificada
              </button>
            )}
          </div>
          <div>
            {company.description && (
              <p className={styles.description}>{company.description}</p>
            )}
          </div>
        </div>
      </section>
      {reviews}
      <section id="contato" className={styles.commercial}>
        <div className={`${styles.container} ${styles.commercialRow}`}>
          <div>
            <p className={styles.eyebrow}>Vamos conversar</p>
            <h2>O próximo passo é seu.</h2>
            <p>Confira as opções e tire suas dúvidas com a loja.</p>
          </div>
          <div className={styles.heroActions}>
            {hasContact ? (
              contact
            ) : company.email ? (
              <a
                href={`mailto:${company.email}`}
                className={styles.primaryButton}
              >
                Enviar e-mail
              </a>
            ) : null}
            <a href="#catalogo" className={styles.outlineButton}>
              Consultar estoque <ArrowRight size={18} aria-hidden="true" />
            </a>
          </div>
        </div>
      </section>
      <div className={styles.container}>{campaign("before_footer")}</div>
      <AutomotiveFooter
        company={company}
        primaryColor={primaryColor}
        formattedCnpj={formattedCnpj}
        address={address}
        attendanceLabel={attendanceLabel}
        instagramUrl={instagramUrl}
        facebookUrl={facebookUrl}
        hasFeatured={featured.length > 0}
        onFinancing={() => onVehicleLead("financing")}
        onAppraisal={() => onVehicleLead("vehicle_appraisal")}
      />
    </div>
  );
}
